import { isPreviewExperimentOverrideHost } from '../../utils/experimentOverrideHosts';
import React, { useEffect, useMemo, useState } from 'react';
import { useThemeMode } from '../../design-system/theme';
import { Helmet } from 'react-helmet-async';
import { Container } from 'react-bootstrap';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '../../design-system';
import { pushEvent, ANALYTICS_EVENTS } from '../../utils/analytics';
import { getUtmParamsForApi } from '../../utils/utmTracker';
import AuthFirstSalesPage from './AuthFirstSalesPage';
import useSalesPromptRouting from '../../hooks/useSalesPromptRouting';
import useABTest from '../../hooks/useABTest';
import { ACTIVE_EXPERIMENTS } from '../../utils/experiments';
import botspotLogo from '../../assets/botspot_logo_horizontal.png';
import AuthFirstDialog from './AuthFirstDialog';
import {
  GOOGLE_SEARCH_JOBS,
  GOOGLE_SEARCH_LP_PATH,
  GOOGLE_SEARCH_SIGNUP_EXPERIENCE_EXPERIMENT_NAME,
  GOOGLE_SEARCH_SIGNUP_EXPERIENCE_FEATURE_FLAG,
  GOOGLE_SEARCH_SIGNUP_EXPERIENCE_PREVIEW_PARAM,
  GOOGLE_SEARCH_SIGNUP_EXPERIENCE_VARIANTS,
  getGoogleSearchJob,
  normalizeGoogleSearchSignupExperienceVariant,
  resolveGoogleSearchJobId,
} from './googleSearchLandingCopy';

import './GoogleSearchLandingBase.css';
import './GoogleSearchLandingTheme.css';
import './AuthFirstSalesPage.css';
import './GoogleSearchLandingPage.css';

function GoogleSearchHeadline({ accent, accentClassName, headline }) {
  // Highlight the accent inside the headline. Do not append it again.
  // "too hard" used to render twice because the old suffix check missed "?".
  if (!accent || !headline.includes(accent)) {
    return headline;
  }

  const accentIndex = headline.indexOf(accent);
  return (
    <>
      {headline.slice(0, accentIndex)}
      <span className={accentClassName}>{accent}</span>
      {headline.slice(accentIndex + accent.length)}
    </>
  );
}

const GOOGLE_SEARCH_SIGNUP_EXPERIENCE =
  ACTIVE_EXPERIMENTS.GOOGLE_SEARCH_AUTH_FIRST_PERSONALIZATION_2026_08;

const buildExperimentReturnTo = ({ baseReturnTo, experimentContext, jobId, searchParams }) => {
  const origin = typeof window !== 'undefined' ? window.location.origin : 'https://botspot.trade';
  const parsed = new URL(baseReturnTo || '/sales-choose', origin);
  const params = parsed.searchParams;

  Object.entries(experimentContext).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      params.set(key, String(value));
    }
  });
  params.set('google_search_lp_job', jobId);

  [
    'utm_source',
    'utm_medium',
    'utm_campaign',
    'utm_content',
    'utm_term',
    'postSignupCheckoutVariant',
  ].forEach((key) => {
    const value = searchParams.get(key);
    if (value && !params.has(key)) {
      params.set(key, value);
    }
  });

  const query = params.toString();
  return `${parsed.pathname}${query ? `?${query}` : ''}${parsed.hash}`;
};

export default function GoogleSearchLandingPage() {
  const [websiteTheme] = useThemeMode();
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [genericAuthOpen, setGenericAuthOpen] = useState(true);
  const { loginReturnTo, newSignupReturnTo, seedPromptIntent } = useSalesPromptRouting({
    pageVariant: 'google_search_lp',
  });

  const utmData = useMemo(() => getUtmParamsForApi(), []);
  const jobId = resolveGoogleSearchJobId({
    job: searchParams.get('job'),
    utmCampaign: searchParams.get('utm_campaign') || utmData?.utm_campaign,
    prompt: searchParams.get('prompt'),
  });
  const job = getGoogleSearchJob(jobId);
  const rawPreviewVariant = searchParams.get(GOOGLE_SEARCH_SIGNUP_EXPERIENCE_PREVIEW_PARAM);
  const previewVariant =
    isPreviewExperimentOverrideHost() &&
    rawPreviewVariant &&
    GOOGLE_SEARCH_SIGNUP_EXPERIENCE_VARIANTS[rawPreviewVariant]
      ? rawPreviewVariant
      : null;
  const experimentName =
    GOOGLE_SEARCH_SIGNUP_EXPERIENCE?.name || GOOGLE_SEARCH_SIGNUP_EXPERIENCE_EXPERIMENT_NAME;
  const featureFlag =
    GOOGLE_SEARCH_SIGNUP_EXPERIENCE?.featureFlag || GOOGLE_SEARCH_SIGNUP_EXPERIENCE_FEATURE_FLAG;
  const experimentTrackingData = useMemo(
    () => ({
      forcedVariant: previewVariant,
      google_search_lp_job: jobId,
      // Disabled/paused flags must stay inactive. Never manufacture control exposure.
      normalizeControlExposure: false,
      skipFeatureFlagEvaluation: Boolean(previewVariant),
      source: location.pathname,
      trackForcedExposure: false,
      validVariants: Object.keys(GOOGLE_SEARCH_SIGNUP_EXPERIENCE_VARIANTS),
      ...utmData,
    }),
    [jobId, location.pathname, previewVariant, utmData],
  );
  const {
    variant: assignedVariant,
    isLoading: isExperimentLoading,
    isExperimentActive,
  } = useABTest(experimentName, featureFlag, 'generic_auth_first', experimentTrackingData);
  const variantId = normalizeGoogleSearchSignupExperienceVariant(previewVariant || assignedVariant);
  const hasResolvedExperiment = Boolean(previewVariant) || !isExperimentLoading;
  const isCustomizedAuthFirst = variantId === 'customized_auth_first';
  const salesPageDesignVariant = isCustomizedAuthFirst
    ? 'google_search_customized_auth_first'
    : 'google_search_generic_auth_first';
  const experimentContext = useMemo(
    () => ({
      experiment_name: experimentName,
      experiment_variant: variantId,
      feature_flag: featureFlag,
      [`$feature/${featureFlag}`]: variantId,
      [`$feature_flag_${featureFlag}`]: variantId,
      google_search_lp_job: jobId,
      google_search_auth_first_personalization_2026_08_experiment_name: experimentName,
      google_search_auth_first_personalization_2026_08_variant: variantId,
      sales_page_design_variant: salesPageDesignVariant,
    }),
    [experimentName, featureFlag, jobId, salesPageDesignVariant, variantId],
  );
  const authReturnTo = useMemo(
    () =>
      buildExperimentReturnTo({
        baseReturnTo: loginReturnTo,
        experimentContext,
        jobId,
        searchParams,
      }),
    [experimentContext, jobId, loginReturnTo, searchParams],
  );
  const authNewSignupReturnTo = useMemo(
    () =>
      buildExperimentReturnTo({
        baseReturnTo: newSignupReturnTo,
        experimentContext,
        jobId,
        searchParams,
      }),
    [experimentContext, jobId, newSignupReturnTo, searchParams],
  );

  useEffect(() => {
    const existingPrompt = (searchParams.get('prompt') || '').trim();
    const existingJob = (searchParams.get('job') || '').trim().toLowerCase();
    if (existingPrompt && existingJob === jobId) {
      return;
    }

    const params = new URLSearchParams(searchParams.toString());
    if (!existingJob || !GOOGLE_SEARCH_JOBS[existingJob] || existingJob !== jobId) {
      params.set('job', jobId);
    }
    if (!existingPrompt && job.defaultPrompt) {
      params.set('prompt', job.defaultPrompt);
    }

    const nextQuery = params.toString();
    const currentQuery = searchParams.toString();
    if (nextQuery !== currentQuery) {
      navigate(`${GOOGLE_SEARCH_LP_PATH}?${nextQuery}`, { replace: true });
    }
  }, [job.defaultPrompt, jobId, navigate, searchParams]);

  useEffect(() => {
    if (typeof seedPromptIntent === 'function') {
      seedPromptIntent();
    }
  }, [seedPromptIntent, searchParams]);

  useEffect(() => {
    window.__experimentContext = experimentContext;
  }, [experimentContext]);

  useEffect(() => {
    if (!hasResolvedExperiment) {
      return;
    }

    pushEvent({
      event: ANALYTICS_EVENTS.PAGE_VIEW,
      page: 'google_search_lp',
      source: location.pathname,
      variant: variantId,
      job: jobId,
      is_experiment_active: previewVariant ? false : isExperimentActive,
      experiment_preview: Boolean(previewVariant),
      ...experimentContext,
      ...utmData,
    });
  }, [
    experimentContext,
    hasResolvedExperiment,
    isExperimentActive,
    jobId,
    location.pathname,
    previewVariant,
    utmData,
    variantId,
  ]);

  const handleGenericAuthOpen = () => {
    pushEvent({
      event: ANALYTICS_EVENTS.LOGIN_CLICKED,
      method: 'google_search_generic_auth_dialog',
      source: location.pathname,
      returnTo: authReturnTo,
      google_search_lp_job: jobId,
      experiment_variant: variantId,
    });
    setGenericAuthOpen(true);
  };

  const headline = (
    <GoogleSearchHeadline
      accent={job.headlineAccent}
      accentClassName={isCustomizedAuthFirst ? undefined : 'v07-gradient-text'}
      headline={job.headline}
    />
  );

  if (isCustomizedAuthFirst) {
    return (
      <>
        <Helmet>
          <title>{`${job.headline} | BotSpot`}</title>
          <meta name="description" content={job.subhead} />
          <meta name="robots" content="noindex, follow" />
          <link rel="canonical" href={`https://botspot.trade${GOOGLE_SEARCH_LP_PATH}`} />
          <meta name="theme-color" content="#080b18" />
        </Helmet>

        <div
          className="google-search-auth-first-page auth-first-sales auth-page bsui-scope"
          data-bs-theme={websiteTheme}
          data-design="google-search-job"
          data-experiment-loading={String(isExperimentLoading)}
          data-job={jobId}
          data-signup-experience={variantId}
          data-testid="google-search-lp"
        >
          <main className="google-search-auth-first__frame">
            <header className="google-search-auth-first__masthead">
              <a aria-label="BotSpot home" href="/">
                <img alt="BotSpot by LumiWealth" height={429} src={botspotLogo} width={1385} />
              </a>
              <span>botspot.trade</span>
            </header>

            <div className="google-search-auth-first__grid">
              <section
                aria-labelledby="google-search-auth-first-title"
                className="google-search-auth-first__pitch"
                data-testid="google-search-auth-first-pitch"
              >
                <h1
                  className="google-search-auth-first__title"
                  data-testid="google-search-lp-headline"
                  id="google-search-auth-first-title"
                >
                  {headline}
                </h1>

                <p
                  className="google-search-auth-first__subhead"
                  data-testid="google-search-lp-subhead"
                >
                  {job.subhead}
                </p>

                {job.proof.length > 0 ? (
                  <ul
                    className="google-search-auth-first__proof"
                    data-testid="google-search-lp-proof"
                  >
                    {job.proof.map((item) => (
                      <li key={item}>{item}</li>
                    ))}
                  </ul>
                ) : null}

                {job.authFirst.steps.length > 0 ? (
                  <div className="google-search-auth-first__workflow">
                    <div className="google-search-auth-first__workflow-copy">
                      <strong>{job.authFirst.evidenceLabel}</strong>
                      <span>{job.authFirst.evidenceDescription}</span>
                    </div>
                    <span className="google-search-auth-first__workflow-meta">
                      {job.authFirst.evidenceMeta}
                    </span>
                    <div className="google-search-auth-first__workflow-steps">
                      {job.authFirst.steps.map((step) => (
                        <span key={step}>{step}</span>
                      ))}
                    </div>
                  </div>
                ) : null}
              </section>

              <div className="google-search-auth-first__auth">
                <AuthFirstSalesPage
                  analyticsContext={experimentContext}
                  authSourceOverride="google_search_customized_auth_first"
                  entryMode="signup"
                  newSignupReturnTo={authNewSignupReturnTo}
                  presentation="embedded"
                  returnTo={authReturnTo}
                />
              </div>
            </div>
          </main>
        </div>
      </>
    );
  }

  return (
    <>
      <Helmet>
        <title>{`${job.headline} | BotSpot`}</title>
        <meta name="description" content={job.subhead} />
        <meta name="robots" content="noindex, follow" />
        <link rel="canonical" href={`https://botspot.trade${GOOGLE_SEARCH_LP_PATH}`} />
      </Helmet>

      <div
        className="sales-page-minimal v07-assistant-theme google-search-lp bsui-scope"
        data-bs-theme={websiteTheme}
        data-experiment-loading={String(isExperimentLoading)}
        data-job={jobId}
        data-signup-experience={variantId}
        data-testid="google-search-lp"
      >
        <Container>
          <div className="content-wrapper v07-centered-layout">
            <div className="hero-content">
              <h1 className="hero-title v07-headline" data-testid="google-search-lp-headline">
                {headline}
              </h1>

              <p className="hero-subtitle v07-sub-headline" data-testid="google-search-lp-subhead">
                {job.subhead}
              </p>

              {job.proof.length > 0 ? (
                <ul className="google-search-lp__proof" data-testid="google-search-lp-proof">
                  {job.proof.map((item) => (
                    <li key={item}>{item}</li>
                  ))}
                </ul>
              ) : null}

              <div className="v07-form-glass-card google-search-lp__generic-auth-launcher">
                <Button
                  fullWidth
                  onClick={handleGenericAuthOpen}
                  size="lg"
                  type="button"
                  variant="primary"
                >
                  Continue
                </Button>
              </div>
            </div>
          </div>
        </Container>
        <AuthFirstDialog
          analyticsContext={experimentContext}
          authSourceOverride="google_search_generic_auth_first"
          entryMode="login"
          newSignupReturnTo={authNewSignupReturnTo}
          onOpenChange={setGenericAuthOpen}
          open={genericAuthOpen}
          returnTo={authReturnTo}
        />
      </div>
    </>
  );
}
